import { useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useToast } from '@crisellevaldez/speculo-ui';
import { createCloudRunAxios } from '../../api/axios';
import { TwilioBalanceResponse } from '../../types/twilio.types';

const remiApi = createCloudRunAxios('remi-api');

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string }>;
    const detail = axiosError.response?.data?.detail;
    if (detail) return detail;
  }
  return fallback;
};

export const useTwilioOperations = () => {
  const { showToast } = useToast();
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [isFetchingBalance, setIsFetchingBalance] = useState(false);
  const [isFetchingNumbers, setIsFetchingNumbers] = useState(false);

  const fetchTwilioBalance = async (sid: string, token: string) => {
    if (!sid || !token) {
      showToast({
        message: 'Please enter SID and Token first',
        variant: 'error',
      });
      return null;
    }

    try {
      setIsFetchingBalance(true);
      const response = await axios.get<TwilioBalanceResponse>(
        `https://api.twilio.com/2010-04-01/Accounts/${sid}/Balance.json`,
        {
          headers: {
            Authorization: `Basic ${btoa(`${sid}:${token}`)}`,
          },
        }
      );
      return response.data.balance;
    } catch (error) {
      console.error('Error fetching Twilio balance:', error);
      return null;
    } finally {
      setIsFetchingBalance(false);
    }
  };

  const fetchPhoneNumbers = async (sid: string, token: string, organizationId?: string) => {
    if (!sid || !token || !organizationId) {
      showToast({
        message: organizationId
          ? 'Please enter SID and Token first'
          : 'Organization is required before fetching phone numbers',
        variant: 'error',
      });
      return [];
    }

    try {
      setIsFetchingNumbers(true);
      const response = await remiApi.post<{
        incoming_phone_numbers: Array<{
          friendlyName?: string;
          phoneNumber?: string;
          friendly_name?: string;
          phone_number?: string;
        }>;
      }>('/api/v1/twilio/phone-numbers', {
        organization_id: organizationId,
        account_sid: sid,
        auth_token: token,
      });
      return response.data.incoming_phone_numbers.map((num) => ({
        friendlyName:
          num.friendlyName ||
          num.friendly_name ||
          num.phoneNumber ||
          num.phone_number ||
          'Twilio number',
        phoneNumber: num.phoneNumber || num.phone_number || '',
      }));
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      showToast({
        message: getErrorMessage(
          error,
          'Failed to fetch phone numbers. Please check your credentials.'
        ),
        variant: 'error',
      });
      return [];
    } finally {
      setIsFetchingNumbers(false);
    }
  };

  const generateKey = async (sid: string, token: string, organizationId?: string) => {
    if (!sid || !token || !organizationId) {
      showToast({
        message: organizationId
          ? 'Please enter SID and Token first'
          : 'Organization is required before generating a key',
        variant: 'error',
      });
      return null;
    }

    try {
      setIsGeneratingKey(true);
      const response = await remiApi.post<{ encrypted_key: string }>('/api/v1/twilio/bland-key', {
        organization_id: organizationId,
        account_sid: sid,
        auth_token: token,
      });
      showToast({
        message: 'Key generated successfully',
        variant: 'success',
      });
      return response.data.encrypted_key;
    } catch (error) {
      console.error('Error generating key:', error);
      showToast({
        message: getErrorMessage(error, 'Failed to generate key. Please check your credentials.'),
        variant: 'error',
      });
      return null;
    } finally {
      setIsGeneratingKey(false);
    }
  };

  return {
    fetchTwilioBalance,
    fetchPhoneNumbers,
    generateKey,
    isGeneratingKey,
    isFetchingBalance,
    isFetchingNumbers,
  };
};
